import { z } from 'zod';
/**
 * Remediation — turning a finding into a proposed code change, and proving afterwards that the
 * change worked.
 *
 * Every change here is a **data edit decided by fixed rules**, never code written by a model:
 *
 * - A **dependency upgrade** sets one package in `package.json` to the lowest version that the
 *   knowledge base's advisories say fixes every CVE the scanner linked to it.
 * - A **security header** sets one entry in a JSON headers file the application reads at start-up
 *   (`guardian/security-headers.json` by default). Guardian edits that file only, so the change is
 *   the same shape in any stack and a reviewer can read it at a glance.
 *
 * The same findings and the same facts always produce the same edit. What the person reviewing
 * the pull request decides is whether to merge it; Guardian never merges.
 */
export const REMEDIATION_RECIPES_VERSION = '2026-10-09.1';
export const RemediationKindSchema = z.enum(['SECURITY_HEADERS', 'DEPENDENCY_UPGRADE']);
/** The ZAP rule that names a vulnerable JavaScript library and its version (retire.js). */
export const DEPENDENCY_RULE_ID = '10003';
/**
 * Header values Guardian proposes. Each is a widely recommended baseline rather than a value
 * tuned to one application; the pull request says so, and the reviewer owns the final value.
 *
 * The CSP allows inline script and style. A policy without them breaks most applications on first
 * deploy, and a fix that breaks the application does not get merged. It still blocks third-party
 * script, plugins, framing by other origins and base-tag injection, and the scanner will then
 * report its remaining weaknesses (rule 10055) as a separate, honest finding to tighten next.
 */
export const HEADER_RECIPES = [
    {
        pluginId: '10038',
        header: 'Content-Security-Policy',
        value: "default-src 'self'; script-src 'self' 'unsafe-inline'; style-src 'self' 'unsafe-inline' https:; font-src 'self' https: data:; img-src 'self' https: data:; connect-src 'self' wss:; frame-ancestors 'self'; object-src 'none'; base-uri 'self'; form-action 'self'",
        rationale: 'Restricts where script, styles and frames may come from (CWE-693).',
    },
    {
        pluginId: '10035',
        header: 'Strict-Transport-Security',
        value: 'max-age=31536000; includeSubDomains',
        rationale: 'Tells browsers to use HTTPS only for a year, so a downgrade cannot strip TLS (CWE-319).',
    },
    {
        pluginId: '10021',
        header: 'X-Content-Type-Options',
        value: 'nosniff',
        rationale: 'Stops browsers guessing a content type and executing an upload as script (CWE-693).',
    },
    {
        pluginId: '10020',
        header: 'X-Frame-Options',
        value: 'SAMEORIGIN',
        rationale: 'Stops other sites framing the application for clickjacking (CWE-1021).',
    },
    {
        pluginId: '10063',
        header: 'Permissions-Policy',
        value: 'camera=(), microphone=(), geolocation=(), payment=()',
        rationale: 'Turns off powerful browser features the application does not use (CWE-693).',
    },
];
const HEADER_BY_RULE = new Map(HEADER_RECIPES.map((r) => [r.pluginId, r]));
export function headerRecipeFor(pluginId) {
    return pluginId ? HEADER_BY_RULE.get(pluginId) : undefined;
}
/** Statuses a person has decided against fixing, or that are fixed already. Never proposed. */
const NOT_PROPOSED = new Set([
    'FALSE_POSITIVE',
    'ACCEPTED_RISK',
    'FIXED',
]);
export function isProposable(status) {
    return !NOT_PROPOSED.has(status);
}
// ---------------------------------------------------------------------------------------------
// Versions
const VERSION = /^v?(\d+)(?:\.(\d+))?(?:\.(\d+))?(?:-([0-9A-Za-z.-]+))?(?:\+[0-9A-Za-z.-]+)?$/;
/** Parses `1.2.3`, `1.2`, `v1.2.3-rc.1`. Null for anything else, including ranges. */
export function parseVersion(value) {
    const m = VERSION.exec(value.trim());
    if (!m)
        return null;
    return {
        major: Number(m[1]),
        minor: Number(m[2] ?? 0),
        patch: Number(m[3] ?? 0),
        ...(m[4] ? { pre: m[4] } : {}),
    };
}
/**
 * Semver precedence for plain versions: negative when `a` is older. A pre-release sorts before
 * its release. Unparseable input throws — a version Guardian cannot order is not one it proposes.
 */
export function compareVersions(a, b) {
    const x = parseVersion(a);
    const y = parseVersion(b);
    if (!x || !y)
        throw new Error(`Cannot compare versions ${a} and ${b}`);
    for (const key of ['major', 'minor', 'patch']) {
        if (x[key] !== y[key])
            return x[key] - y[key];
    }
    if (x.pre === y.pre)
        return 0;
    if (x.pre === undefined)
        return 1;
    if (y.pre === undefined)
        return -1;
    return x.pre < y.pre ? -1 : 1;
}
export const headersKey = () => 'HEADERS';
export const dependencyKey = (c) => `${c.ecosystem.toUpperCase()}#${c.name.toLowerCase()}`;
/**
 * The lowest version that fixes every CVE that has a known fix, or null.
 *
 * An advisory can name a fixed version per release line (1.12.0 and 3.5.0 for the same flaw), so
 * for each CVE the first fix *above the installed version* is the one that counts; the answer is
 * the highest of those. A CVE whose every fix is at or below the installed version is not one
 * this upgrade is for, and is left out rather than counted as fixed.
 */
export function upgradeFor(component, cves) {
    if (!parseVersion(component.version))
        return null;
    const name = component.name.toLowerCase();
    let target = null;
    const fixesCveIds = [];
    const kevCveIds = [];
    const unresolvedCveIds = [];
    const advisoryIds = new Set();
    for (const cve of cves) {
        const fixes = cve.fixedIn
            .filter((f) => f.ecosystem.toLowerCase() === 'npm' && f.package.toLowerCase() === name)
            .filter((f) => parseVersion(f.fixed) !== null)
            .filter((f) => compareVersions(f.fixed, component.version) > 0)
            .sort((a, b) => compareVersions(a.fixed, b.fixed));
        const first = fixes[0];
        if (!first) {
            unresolvedCveIds.push(cve.cveId);
            continue;
        }
        fixesCveIds.push(cve.cveId);
        if (cve.kev)
            kevCveIds.push(cve.cveId);
        advisoryIds.add(first.advisoryId);
        if (target === null || compareVersions(first.fixed, target) > 0)
            target = first.fixed;
    }
    if (target === null)
        return null;
    return {
        to: target,
        fixesCveIds: fixesCveIds.sort(),
        kevCveIds: kevCveIds.sort(),
        unresolvedCveIds: unresolvedCveIds.sort(),
        advisoryIds: [...advisoryIds].sort(),
    };
}
/**
 * Decides what to propose for an engagement's findings.
 *
 * `intel` holds the knowledge base's CVE records per finding id, or null when the knowledge base
 * is not available. Pure: the caller fetches, this decides.
 */
export function planRemediations(findings, intel) {
    const plans = [];
    const deferred = [];
    const skipped = [];
    const open = findings.filter((f) => isProposable(f.status));
    // Headers: one pull request for every missing header, because they are one file.
    const headerChanges = new Map();
    for (const f of open) {
        const recipe = headerRecipeFor(f.classification?.pluginId ?? f.ruleId);
        if (!recipe)
            continue;
        const change = headerChanges.get(recipe.header) ?? {
            pluginId: recipe.pluginId,
            header: recipe.header,
            value: recipe.value,
            rationale: recipe.rationale,
            findingIds: [],
        };
        change.findingIds.push(f.id);
        headerChanges.set(recipe.header, change);
    }
    if (headerChanges.size > 0) {
        const headers = [...headerChanges.values()].sort((a, b) => a.header.localeCompare(b.header));
        plans.push({
            key: headersKey(),
            kind: 'SECURITY_HEADERS',
            findingIds: headers.flatMap((h) => h.findingIds).sort(),
            headers,
        });
    }
    // Dependencies: one pull request per package, so each upgrade can be reviewed and reverted alone.
    const byPackage = new Map();
    for (const f of open) {
        const component = f.classification?.component;
        if ((f.classification?.pluginId ?? f.ruleId) !== DEPENDENCY_RULE_ID)
            continue;
        if (!component) {
            skipped.push({ findingId: f.id, reason: 'The scanner did not name a library and version' });
            continue;
        }
        const key = dependencyKey(component);
        const entry = byPackage.get(key) ?? { component, findings: [] };
        entry.findings.push(f);
        byPackage.set(key, entry);
    }
    for (const [key, { component, findings: group }] of byPackage) {
        const ids = group.map((f) => f.id).sort();
        const cveSets = group.map((f) => intel.get(f.id));
        if (cveSets.some((c) => c === null || c === undefined)) {
            for (const id of ids)
                skipped.push({ findingId: id, reason: 'The knowledge base is not available' });
            continue;
        }
        const cves = [...new Map(cveSets.flatMap((c) => c ?? []).map((c) => [c.cveId, c])).values()];
        if (cves.some((c) => c.status === 'PENDING')) {
            deferred.push(...ids);
            continue;
        }
        const upgrade = upgradeFor(component, cves.filter((c) => c.status === 'LOADED'));
        if (!upgrade) {
            for (const id of ids)
                skipped.push({
                    findingId: id,
                    reason: 'No advisory names a fixed version above the installed one',
                });
            continue;
        }
        plans.push({
            key,
            kind: 'DEPENDENCY_UPGRADE',
            findingIds: ids,
            dependency: {
                ecosystem: 'npm',
                name: component.name.toLowerCase(),
                from: component.version,
                ...upgrade,
            },
        });
    }
    return { plans, deferred: deferred.sort(), skipped };
}
function indentOf(text) {
    const m = /\n( +)"/.exec(text);
    return m?.[1]?.length ?? 2;
}
const DEPENDENCY_SECTIONS = ['dependencies', 'devDependencies', 'optionalDependencies'];
/**
 * Sets one package's version in a `package.json`, exactly, wherever it is declared.
 *
 * Exact rather than a caret range: the point is that the deployed version is the fixed one, and
 * `^3.5.0` lets a lockfile-less install resolve whatever is newest that day. Key order and
 * indentation are kept, so the diff is the one line that changed.
 */
export function editPackageJson(path, text, change) {
    const manifest = JSON.parse(text);
    let found = false;
    for (const section of DEPENDENCY_SECTIONS) {
        const deps = manifest[section];
        if (deps && typeof deps === 'object' && !Array.isArray(deps)) {
            const record = deps;
            const name = Object.keys(record).find((k) => k.toLowerCase() === change.name);
            if (name) {
                found = true;
                record[name] = change.to;
            }
        }
    }
    if (!found)
        throw new Error(`${change.name} is not declared in ${path}`);
    const content = `${JSON.stringify(manifest, null, indentOf(text))}${text.endsWith('\n') ? '\n' : ''}`;
    return { path, content, changed: content !== text };
}
export const HeadersFileSchema = z.object({
    headers: z.record(z.string().regex(/^[A-Za-z0-9-]{1,100}$/), z.string().max(2000)),
});
const HEADERS_FILE_NOTE = 'Response headers the application sets on every response. Proposed changes come from Guardian AI as pull requests; edit freely.';
/**
 * Sets headers in the application's headers file, creating it if absent. Names compare without
 * case, as HTTP does; an existing entry is replaced rather than duplicated.
 */
export function editHeadersFile(path, text, headers) {
    const current = text === null ? { headers: {} } : HeadersFileSchema.parse(JSON.parse(text));
    const next = { ...current.headers };
    for (const { header, value } of headers) {
        const existing = Object.keys(next).find((k) => k.toLowerCase() === header.toLowerCase());
        if (existing && existing !== header)
            delete next[existing];
        next[existing ?? header] = value;
    }
    // A new file says what it is for; an existing one keeps whatever else it holds, unchanged.
    const body = text === null
        ? { $comment: HEADERS_FILE_NOTE, headers: next }
        : { ...JSON.parse(text), headers: next };
    const content = `${JSON.stringify(body, null, text === null ? 2 : indentOf(text))}\n`;
    return { path, content, changed: content !== text };
}
//# sourceMappingURL=remediation.js.map